'use client';

import React, { useRef } from 'react';
import { Grid, Box, Typography, Paper, Fade, Button } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useEstimator } from '../../context/EstimatorContext';
import ProjectBudgetSummary from './ProjectBudgetSummary';
import LogoImage from '../../assets/images/wwm-logo-2.png';
import ProjectMetricsSummary from './ProjectMetricsSummary';
import CostAssumptionsSummary from './CostAssumptionsSummary';
import ProjectQuantitiesSummary from './ProjectQuantitiesSummary';
import AdditionalInfoSummary from './GanntSummary';

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const Summary = () => {
  const { formData } = useEstimator();
  const pdfRef = useRef(); // 👈 Create a ref

  console.log('Full Context Summary:', formData);
  const handleExportPdf = async () => {
    try {
      const element = pdfRef.current;
      if (!element) return;

      // ➡️ Add a special class to clean up color() css
      element.classList.add('force-basic-color');

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });

      // ➡️ Remove class afterward
      element.classList.remove('force-basic-color');

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save('summary.pdf');
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  return (
    <Fade in={true}>
      <Box>
        {/* 📤 Export Button */}
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button variant="contained" color="primary" onClick={handleExportPdf}>
            Export PDF
          </Button>
        </Box>

        {/* 🖼️ The entire section you want to export */}
        <Box ref={pdfRef}>
          {/* First Grid: Project Info and Additional Info */}
          <Grid container spacing={4}>
            {/* Left Paper - Logo + Project Info */}
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  position: 'relative',
                  p: 3,
                  mb: 3,
                  backgroundColor: '#F9F9F9',
                  borderLeft: '4px solid #223A64',
                }}
              >
                <Box
                  component="img"
                  src={LogoImage}
                  alt="Logo"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    width: 200,
                    height: 'auto',
                  }}
                />

                <Grid container spacing={4} sx={{ mt: 4 }}>
                  <Grid item xs={12} md={6} sx={{ pt: 0 }}>
                    <Box mb={2} display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                        Project Name:
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {formData.projectName || '—'}
                      </Typography>
                    </Box>

                    <Box mb={2} display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                        Project Scope:
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {formData.projectScope || '—'}
                      </Typography>
                    </Box>

                    <Box mb={2} display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                        Date:
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {formData.date || '—'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6} sx={{ pt: 0 }}>
                    <Box mb={2} display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                        Estimated By:
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {formData.estimatedBy || '—'}
                      </Typography>
                    </Box>

                    <Box mb={2} display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                        Requested By:
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {formData.requestedBy || '—'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Right Paper */}
            <AdditionalInfoSummary />
          </Grid>
          <Grid container spacing={4} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: 0 }}>
                <ProjectBudgetSummary />
              </Box>
            </Grid>

            {/* Right Side - Other Summaries */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <ProjectMetricsSummary />
                </Grid>
                <Grid item xs={12}>
                  <CostAssumptionsSummary />
                </Grid>
                <Grid item xs={12}>
                  <ProjectQuantitiesSummary />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Fade>
  );
};

export default Summary;
